import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { Pencil, Check, RotateCcw, X, Type } from "lucide-react";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT", "SELECT", "OPTION", "SVG", "PATH", "CODE", "PRE"]);
const origMap = new WeakMap();

function eachTextNode(cb) {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const t = node.nodeValue;
      if (!t || !t.trim()) return NodeFilter.FILTER_REJECT;
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      if (p.closest("[data-no-tr]")) return NodeFilter.FILTER_REJECT;
      if (p.isContentEditable) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) cb(n);
}

export default function TextCustomizer() {
  const { settings, reload } = useSettings();
  const { user } = useAuth();
  const overrides = settings?.overrides || {};
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  const [editing, setEditing] = useState(false);
  const [pop, setPop] = useState(null); // {x,y,key,value}
  const [val, setVal] = useState("");

  const apply = useCallback(() => {
    const ov = overridesRef.current || {};
    eachTextNode((node) => {
      if (!origMap.has(node)) origMap.set(node, node.nodeValue.trim());
      const key = origMap.get(node);
      const target = ov[key];
      if (target != null && node.nodeValue.trim() !== target) {
        const m = node.nodeValue.match(/^(\s*)([\s\S]*?)(\s*)$/);
        node.nodeValue = (m ? m[1] : "") + target + (m ? m[3] : "");
      }
    });
  }, []);

  // Applica gli override per TUTTI gli utenti + osserva i cambi del DOM
  useEffect(() => {
    apply();
    if (!Object.keys(overrides).length) return;
    let raf = null;
    const obs = new MutationObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; apply(); });
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [overrides, apply]);

  // In modalità modifica: un CLICK NORMALE sul testo apre l'editor (affidabile anche su tablet/telefono)
  useEffect(() => {
    if (!editing) return;
    document.body.classList.add("tc-editing");
    const handler = (e) => {
      if (e.target.closest && e.target.closest("[data-tc-popover]")) return;
      if (e.target.closest && e.target.closest("[data-testid='text-edit-toggle']")) return;
      let node = null;
      if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (r && r.startContainer && r.startContainer.nodeType === 3) node = r.startContainer;
      } else if (document.caretPositionFromPoint) {
        const p = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (p && p.offsetNode && p.offsetNode.nodeType === 3) node = p.offsetNode;
      }
      if (!node || !node.nodeValue || !node.nodeValue.trim()) {
        const el = e.target;
        node = el && [...(el.childNodes || [])].find((c) => c.nodeType === 3 && c.nodeValue.trim());
      }
      if (!node) return;
      const p = node.parentElement;
      if (p && (SKIP_TAGS.has(p.tagName) || p.closest("[data-no-tr]") || p.isContentEditable)) return;
      e.preventDefault();
      e.stopPropagation();
      const key = origMap.get(node) || node.nodeValue.trim();
      if (!origMap.has(node)) origMap.set(node, key);
      const cur = overridesRef.current[key] ?? key;
      setVal(cur);
      setPop({ x: Math.min(e.clientX, window.innerWidth - 340), y: Math.min(e.clientY, window.innerHeight - 220), key });
    };
    document.addEventListener("click", handler, true);
    return () => { document.removeEventListener("click", handler, true); document.body.classList.remove("tc-editing"); };
  }, [editing]);

  const save = async (reset = false) => {
    try {
      await api.put("/settings/override", { key: pop.key, value: reset ? "" : val });
      await reload();
      setPop(null);
      toast.success(reset ? "Testo ripristinato" : "Testo aggiornato");
      setTimeout(apply, 50);
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  if (!user?.is_admin && user?.role !== "admin") return null;

  return (
    <div data-no-tr>
      <button
        data-testid="text-edit-toggle"
        onClick={() => { setEditing((v) => !v); setPop(null); }}
        className={`fixed bottom-5 left-5 z-[60] flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-lg transition-colors ${editing ? "bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700"}`}
        title="Modifica testi con click destro"
      >
        {editing ? <Check className="w-4 h-4" /> : <Type className="w-4 h-4" />}
        {editing ? "Modifica testi ATTIVA" : "Modifica testi"}
      </button>

      {editing && (
        <div className="fixed top-0 left-0 right-0 z-[59] bg-amber-400/95 text-slate-950 text-center text-sm font-semibold py-1.5" data-no-tr>
          Modalità modifica testi attiva — <b>clicca su un testo</b> per personalizzarlo
        </div>
      )}

      {pop && (
        <div
          data-tc-popover
          data-no-tr
          style={{ top: pop.y, left: pop.x }}
          className="fixed z-[70] w-80 bg-slate-900 border border-amber-400/50 rounded-lg shadow-2xl p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-300"><Pencil className="w-3.5 h-3.5" /> Modifica testo</span>
            <button onClick={() => setPop(null)} className="text-slate-400 hover:text-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="text-[11px] text-slate-500 mb-1 truncate">Originale: "{pop.key}"</div>
          <textarea
            data-testid="text-edit-input"
            autoFocus
            rows={3}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-amber-400 resize-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="text-edit-save" onClick={() => save(false)} className="flex-1 bg-amber-400 text-slate-950 font-semibold rounded-md py-1.5 text-sm flex items-center justify-center gap-1 hover:bg-amber-300 transition-colors">
              <Check className="w-4 h-4" /> Salva
            </button>
            <button data-testid="text-edit-reset" onClick={() => save(true)} title="Ripristina originale" className="rounded-md py-1.5 px-2.5 text-sm bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-1">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
